/* eslint-disable react-hooks/rules-of-hooks, complexity */
import PropTypes from 'prop-types';
import React, { useContext, useEffect } from 'react';
import cookies from 'react-cookies';
import { useSelector } from 'react-redux';
import LoyaltyBanner from '../../components/LoyaltyBanner';
import LoyaltyInPageBanner from '../../components/LoyaltyBanner/LoyaltyInPageBanner';
import VoucherItem from '../../components/VoucherItem';
import Message from '../../components/_generic/Message';
import Spinner from '../../components/_generic/spinner/Spinner';
import Tile from '../../components/_generic/tile/Tile';
import TileBody from '../../components/_generic/tile/TileBody';
import TileTitle from '../../components/_generic/tile/TileTitle';
import CheckoutStepper from '../../components/checkout-stepper/CheckoutStepper';
import WowcherVipCue from '../../components/checkout/WowcherVipCue';
import LoadMore from '../../components/deal/ConnectedBottomDeals/LoadMore';
import PasswordActivateModal from '../../components/myaccount/PasswordActivateModal';
import PasswordConfirmEmailModal from '../../components/myaccount/PasswordConfirmEmailModal';
import ValidityDropDown from '../../components/myaccount/ValidityDropDown';
import CreditUnredeemsModal from '../../components/myaccount/vouchers/CreditUnredeemsModal';
import ReferFriendBanner from '../../components/refer-a-friend/ReferFriendBanner';
import { PAGE_TYPES } from '../../config/constants/page-types';
import COOKIES from '../../config/cookies/cookies';
import { LINKS_GLOBAL_AWIN } from '../../config/links/links';
import {
  ALL_VOUCHERS_LOADED,
  BACK_TO_TOP,
  EMPTY_VOUCHERS_MESSAGE,
  MY_WOWCHERS_TITLE,
  ORDER_COMPLETE,
  ORDER_NUMBER_SHORT,
  PAGE_TITLE_MY_VOUCHERS,
} from '../../config/text/text';
import { trackSecurePage } from '../../helpers/analytics';
import { removeCookie } from '../../helpers/cookieSetter';
import { isWowcherBrand } from '../../helpers/pages';
import { usePaymentInfo } from '../../helpers/secondCheckout';
import {
  delayTransactionalEmails,
  useVoucherExtensionFeed,
  useVoucherFeed,
} from '../../helpers/vouchers';
import AccountLayout from '../../layouts/AccountLayout';
import PurchaseContext from '../../providers/PurchaseProvider';
import ThemeContext from '../../providers/ThemeProvider';
import { getServerSideEssentials } from '../../redux/actions/ssr';
import { wrapper } from '../../redux/store/store';

const VoucherPage = ({
  showProgressBar,
  orderIdFromSecondCheckout,
  hasBenefits,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const theme = useContext(ThemeContext);
  const { userprofile } = useSelector((state) => state.user);
  let [orderId] = useContext(PurchaseContext);

  const isExpressRegister = cookies?.load(COOKIES.expressRegister) === 'true';
  if (!orderId) {
    orderId = orderIdFromSecondCheckout;
  }
  const {
    ready,
    vouchers,
    purchases,
    loadMore,
    canLoadMore,
    endOfFeed,
    working,
    count,
    mutate,
  } = useVoucherFeed(orderId);

  const { extensions, mutateExtensions } = useVoucherExtensionFeed(null);
  const paymentInfo = usePaymentInfo();

  const isVip = userprofile?.vipUser;
  const isWowcher = isWowcherBrand();
  const rafEnabled = JSON.parse(process.env.NEXT_PUBLIC_RAF_ENABLED === 'true');

  const handleUpdateFeeds = () => {
    if (mutate && typeof mutate === 'function') mutate();
    if (mutateExtensions && typeof mutateExtensions === 'function')
      mutateExtensions();
  };

  useEffect(() => {
    // get fresh voucher list on subsequent session visits
    if (mutate && typeof mutate === 'function') mutate();
    // get fresh extensiona list
    if (mutateExtensions && typeof mutateExtensions === 'function')
      mutateExtensions();

    if (showProgressBar) {
      const trackingData = {
        pageName: 'second-checkout: mywowchers',
      };

      trackSecurePage(trackingData);
    } else {
      trackSecurePage({
        pageName: 'my account: my vouchers: default',
        section1: 'my account',
        section2: 'my account: my vouchers',
        section3: 'my account: my vouchers: default',
      });
    }

    if (showProgressBar && (orderId || orderIdFromSecondCheckout)) {
      if (orderId) {
        delayTransactionalEmails(orderId);
      } else {
        delayTransactionalEmails(orderIdFromSecondCheckout);
      }
    }
    // Remove cookies and localStorage values from express-checkout if any are left
    if (cookies.load(COOKIES.expressCheckoutEmail)) {
      removeCookie(COOKIES.expressCheckoutEmail);
    }

    localStorage.removeItem(COOKIES.expressAuthToken);
  }, []);

  useEffect(() => {
    const script = document.createElement('script');

    script.src = `${LINKS_GLOBAL_AWIN}`;
    script.async = true;
    script.defer = true;

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  if (!ready) {
    return (
      <Tile title={MY_WOWCHERS_TITLE[process.env.NEXT_PUBLIC_BRAND]}>
        <Spinner display="relative" testId="loading-spinner" />
      </Tile>
    );
  }

  if (count === 0) {
    return (
      <>
        <Tile
          title={
            <TileTitle
              rightNode={
                isVip && (
                  <WowcherVipCue showText={false} userprofile={userprofile} />
                )
              }
              title={MY_WOWCHERS_TITLE[process.env.NEXT_PUBLIC_BRAND]}
            />
          }
        >
          <TileBody>
            <Message
              extraClasses="message-center message-border-radius orange-theme"
              message={EMPTY_VOUCHERS_MESSAGE[process.env.NEXT_PUBLIC_BRAND]}
              showIcon={false}
            />
          </TileBody>
        </Tile>
      </>
    );
  }

  return (
    <>
      {showProgressBar && (
        <CheckoutStepper activeStep="4" hasBenefits={hasBenefits} />
      )}
      <PasswordActivateModal />
      {!isExpressRegister && (
        <PasswordConfirmEmailModal hasJustPurchased={purchases.length > 0} />
      )}
      {purchases.length > 0 && !showProgressBar && <LoyaltyBanner />}
      {showProgressBar && <LoyaltyInPageBanner />}
      {
        <ValidityDropDown
          extensions={extensions}
          mutate={handleUpdateFeeds}
          paymentInfo={paymentInfo}
        />
      }
      {isWowcher && rafEnabled && purchases.length > 0 && (
        <ReferFriendBanner withButton />
      )}
      {purchases.length > 0 && (
        <>
          <CreditUnredeemsModal
            mutate={handleUpdateFeeds}
            purchases={purchases}
          />
          <Tile margin>
            <div className="header">
              <h2 className="title">{ORDER_COMPLETE}</h2>
              <p className="title">
                <span className="bold">{ORDER_NUMBER_SHORT} </span>
                <span>{orderId}</span>
              </p>
            </div>
            <TileBody nobackground>
              {purchases.map((voucher) => {
                return (
                  <VoucherItem
                    isPurchase
                    key={voucher.voucherCode}
                    mutate={mutate}
                    voucher={voucher}
                  />
                );
              })}
            </TileBody>
          </Tile>
        </>
      )}
      {vouchers.length > 0 && (
        <Tile
          title={
            <TileTitle
              rightNode={
                isVip && (
                  <WowcherVipCue showText={false} userprofile={userprofile} />
                )
              }
              title={MY_WOWCHERS_TITLE[process.env.NEXT_PUBLIC_BRAND]}
            />
          }
        >
          <div className="wrapper">
            {vouchers.map((voucher) => (
              <VoucherItem
                key={voucher.voucherCode}
                mutate={mutate}
                voucher={voucher}
              />
            ))}
            {working && <Spinner display="relative" testId="loading-spinner" />}
            {endOfFeed && (
              <div className="loaded">
                <p className="loaded-text">{ALL_VOUCHERS_LOADED(count)}</p>
                <button
                  className="back-to-top"
                  onClick={() =>
                    window.scrollTo({ behavior: 'smooth', top: 0 })
                  }
                  type="button"
                >
                  {BACK_TO_TOP} ↑
                </button>
              </div>
            )}
          </div>
        </Tile>
      )}

      {canLoadMore && <LoadMore onTrigger={loadMore} />}
      <style jsx>{`
        .header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          text-transform: ${theme.text.transform};
          border-radius: 7px 7px 0 0;
          background-color: ${theme.colors.primary};
          color: ${theme.colors.textonprimary};
          padding: 15px;
        }
        .title {
          margin: 0;
        }
        .bold {
          font-weight: bold;
        }
        .wrapper {
          padding: 15px;
        }
        .loaded {
          text-align: center;
          background-color: #f5f5f5;
          padding: 15px;
        }
        .loaded-text {
          font-style: italic;
        }
        .back-to-top {
          border: none;
          background-color: transparent;
          color: ${theme.colors.primary};
        }
        .back-to-top:hover {
          text-decoration: underline;
        }
      `}</style>
    </>
  );
};
VoucherPage.pageType = PAGE_TYPES.vouchers;
VoucherPage.title = PAGE_TITLE_MY_VOUCHERS[process.env.NEXT_PUBLIC_BRAND];
VoucherPage.layout = AccountLayout;
VoucherPage.propTypes = {
  hasBenefits: PropTypes.bool,
  orderIdFromSecondCheckout: PropTypes.string,
  showProgressBar: PropTypes.bool,
};

export const getServerSideProps = wrapper.getServerSideProps(
  async ({ req, store, query }) => {
    await store.dispatch(getServerSideEssentials(req.url)).catch((error) => {
      console.error(`getServerSideEssentials error: ${error}`);
    });
    const showProgressBar = query?.showProgressBar === 'true';
    const hasBenefits = query?.hasBenefits === 'true';
    let orderIdFromSecondCheckout;
    if (query?.orderId) {
      orderIdFromSecondCheckout = query?.orderId;
    } else {
      orderIdFromSecondCheckout = '';
    }

    return {
      props: {
        hasBenefits,
        orderIdFromSecondCheckout,
        showProgressBar,
      },
    };
  },
);

export default VoucherPage;
